import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  QMenuButton,
  QMenuItem,
  QIconButton,
  QBox,
  useToastProvider,
  QToastProps,
  useCurrentUser,
} from '@qualio/ui-components';
import { EventTemplateStepCreateEdit, EventTemplateFormValues } from '../../../types';
import { moveElementInOrderedArray } from '../../../utils/arrayUtils';
import { useFormTemplateDetails } from '../../../hooks';

type StepRowActionsProps = {
  step: EventTemplateStepCreateEdit;
  templateSteps: EventTemplateFormValues['steps'];
  setTemplateSteps: (steps: EventTemplateFormValues['steps']) => void;
  renameTemplateStep: (stepIndex: number) => void;
  changeValidationStepType: (stepIndex: number) => void;
  onDelete: (step: EventTemplateStepCreateEdit) => void;
  disableEdit: boolean;
  isTemplateReadonly: boolean;
};

const makeSaveToast = (title: string, description: string) => {
  return {
    title: title,
    description: description,
    status: 'error',
  } as QToastProps;
};

const StepRowActions = ({
  step,
  templateSteps,
  setTemplateSteps,
  renameTemplateStep,
  changeValidationStepType,
  onDelete,
  disableEdit,
  isTemplateReadonly,
}: StepRowActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const templateId = step.workflow_id;
  const contentTemplateId = step.template_id;
  const formTemplateId = step.form_template_id;
  const stepIsValidationStep = step.order === templateSteps.length;
  const stepCanBeValidationStep = ['document', 'form'].includes(step.type);
  const upButtonDisabled = step.order === 1 || stepIsValidationStep;
  const downButtonDisabled = step.order === templateSteps.length - 1 || stepIsValidationStep;
  const showDelete = !stepIsValidationStep;
  const showEditContentButton = disableEdit ? false : step.type === 'document' && step.template_id;
  const showEditFormButton = disableEdit ? false : step.type === 'form';
  const existingForm = templateId && step.id && formTemplateId && showEditFormButton;

  const { data } = useFormTemplateDetails(
    companyId,
    Number(templateId),
    Number(step.id),
    Number(formTemplateId),
    !!existingForm,
  );

  const formHasFields = data && Array.isArray(data.fields) && data.fields.length;

  const editContentAction = useCallback(() => {
    if (contentTemplateId) {
      navigate(`../templates/${templateId}/content-templates/${contentTemplateId}/edit`);
    } else {
      showToast(
        makeSaveToast(
          t('templates.messages.contentTemplateNotCreated'),
          t('common.notifications.pleaseSaveTheEventTemplateFirst'),
        ),
      );
    }
  }, [contentTemplateId, navigate, showToast, templateId, t]);

  const editFormAction = useCallback(() => {
    if (formTemplateId) {
      navigate(`../templates/${templateId}/form-templates/${formTemplateId}/edit`);
    } else {
      showToast(
        makeSaveToast(
          t('templates.messages.formTemplateNotCreated'),
          t('common.notifications.pleaseSaveTheEventTemplateFirst'),
        ),
      );
    }
  }, [navigate, templateId, showToast, formTemplateId, t]);

  const changeStepOrder = (direction: 'up' | 'down') => {
    setTemplateSteps(moveElementInOrderedArray(templateSteps, step, direction));
  };

  const makeValidationStep = () => {
    setTemplateSteps(moveElementInOrderedArray(templateSteps, step, 'last'));
  };

  const deleteAction = () => {
    onDelete(step);
  };

  const renameAction = () => {
    renameTemplateStep(step.order - 1);
  };

  const changeValidationTypeAction = () => {
    changeValidationStepType(step.order - 1);
  };

  const changeStepOrderUp = () => {
    changeStepOrder('up');
  };

  const changeStepOrderDown = () => {
    changeStepOrder('down');
  };

  return (
    <QBox textAlign="right">
      {stepIsValidationStep ? null : (
        <QIconButton
          data-cy={`arrow-up-${step.id}`}
          iconName="ArrowUp"
          aria-label="moveUp"
          onClick={changeStepOrderUp}
          isDisabled={isTemplateReadonly || upButtonDisabled}
        />
      )}
      {stepIsValidationStep ? null : (
        <QIconButton
          data-cy={`arrow-down-${step.id}`}
          iconName="ArrowDown"
          aria-label="moveDown"
          onClick={changeStepOrderDown}
          isDisabled={isTemplateReadonly || downButtonDisabled}
        />
      )}
      <QMenuButton
        isDisabled={isTemplateReadonly}
        buttonLabel={`step-actions-${step.id}`}
        iconName="MoreVertical"
        itemSize="sm"
        variant="icon"
      >
        <QMenuItem data-cy={`rename-step-action-${step.id}`} onClick={renameAction} aria-label="rename-action">
          {t('templates.actions.renameStep')}
        </QMenuItem>
        {stepIsValidationStep && !templateId ? (
          <QMenuItem
            data-cy={`change-validation-type-action-${step.id}`}
            onClick={changeValidationTypeAction}
            aria-label="change-validation-type-action"
          >
            {t('templates.actions.changeValidationStepType')}
          </QMenuItem>
        ) : null}
        {showEditContentButton ? (
          <QMenuItem
            data-cy={`edit-content-template-action-${step.id}`}
            onClick={editContentAction}
            aria-label="edit-action"
          >
            {t('templates.actions.editContentTemplate')}
          </QMenuItem>
        ) : null}
        {showEditFormButton ? (
          <QMenuItem data-cy={`edit-form-action-${step.id}`} onClick={editFormAction} aria-label="edit-form-action">
            {formHasFields ? t('templates.messages.editFormTemplate') : t('templates.messages.startBuildingYourForm')}
          </QMenuItem>
        ) : null}
        {showDelete ? (
          <QMenuItem data-cy={`delete-step-action-${step.id}`} onClick={deleteAction} aria-label="delete-action">
            {t('templates.actions.deleteStep')}
          </QMenuItem>
        ) : null}
        {!stepIsValidationStep && stepCanBeValidationStep ? (
          <QMenuItem
            data-cy={`make-validation-action-${step.id}`}
            onClick={makeValidationStep}
            aria-label="make-validation-action"
          >
            {t('templates.messages.makeThisTheValidationStep')}
          </QMenuItem>
        ) : null}
      </QMenuButton>
    </QBox>
  );
};

export default StepRowActions;
